import {
  clampProgressPercent,
  clampProgressStep,
  formatProgressStepLabel,
  isOperationProgressWaitingState,
  type OperationProgressLifecycleState,
} from '../../../shared/ui/progress';

export type ValidationRunPhase =
  | 'preparing'
  | 'syncing'
  | 'submitting'
  | 'running'
  | 'processing'
  | 'complete'
  | 'cache-hit'
  | 'error';

export type ValidationRunProgressMetric = {
  label: string;
  value: string;
};

export type ValidationRunProgress = {
  operationId?: string;
  phase: ValidationRunPhase;
  message: string;
  detail?: string;
  currentStep: number;
  totalSteps: number;
  startedAt: number;
  updatedAt: number;
  functionName?: string;
  currentItem?: string;
  state?: OperationProgressLifecycleState;
  waitReason?: string;
  waitElapsedMs?: number | null;
  activeElapsedMs?: number | null;
  processed?: number | null;
  total?: number | null;
  percent?: number | null;
  elapsedMs?: number | null;
  etaMs?: number | null;
  cacheStatus?: string;
  resultReady?: boolean;
  error?: string;
  metrics?: ValidationRunProgressMetric[];
};

export const VALIDATION_RUN_TOTAL_STEPS = 5;

export function clampValidationRunStep(currentStep: number, totalSteps: number): number {
  return clampProgressStep(currentStep, totalSteps, 1);
}

export function getValidationRunElapsedMs(
  progress: ValidationRunProgress | null | undefined,
  now: number = Date.now()
): number {
  if (!progress?.startedAt) {
    return 0;
  }

  return Math.max(0, now - progress.startedAt);
}

export function getValidationRunPercent(
  progress: ValidationRunProgress | null | undefined,
  now: number = Date.now()
): number | null {
  if (!progress) {
    return null;
  }

  if (progress.phase === 'complete' || progress.phase === 'cache-hit') {
    return 100;
  }

  if (progress.phase === 'error') {
    return null;
  }

  if (isOperationProgressWaitingState(progress.state)) {
    return null;
  }

  const explicitPercent = clampProgressPercent(progress.percent);
  if (explicitPercent !== null) {
    return explicitPercent;
  }

  void now;
  return null;
}

export function formatValidationRunDuration(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) {
    return null;
  }

  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }

  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
}

export function formatValidationRunStep(progress: ValidationRunProgress | null | undefined): string | null {
  if (!progress || progress.phase === 'cache-hit') {
    return null;
  }

  return formatProgressStepLabel(progress.currentStep, progress.totalSteps, 1);
}

export function formatValidationRunEta(
  progress: ValidationRunProgress | null | undefined,
  now: number = Date.now()
): string | null {
  if (!progress) {
    return null;
  }

  if (progress.phase === 'cache-hit') {
    return 'Cached report reused';
  }

  if (progress.phase === 'complete') {
    return 'Complete';
  }

  if (progress.phase === 'error') {
    return 'Stopped';
  }

  if (isOperationProgressWaitingState(progress.state)) {
    const waiting = formatValidationRunDuration(progress.waitElapsedMs);
    return waiting ? `Waiting ${waiting}` : 'Waiting';
  }

  const elapsed = formatValidationRunDuration(progress.elapsedMs ?? getValidationRunElapsedMs(progress, now));
  const remaining = formatValidationRunDuration(progress.etaMs);

  if (remaining && elapsed) {
    return `About ${remaining} remaining / ${elapsed} elapsed`;
  }

  return elapsed ? `${elapsed} elapsed` : null;
}
